import { FC } from "react";
import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Button } from "../Button";
import { DataSource } from "../DataSource";
import { Paged } from "../Paged";
import { createComponentStyleHook } from "../../tokens";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

/** The `Pager` component properties. */
export interface PagerProps extends BaseProps {

  /** The data source. */
  readonly dataSource: DataSource<object> & Paged;
}

const useStyles = createComponentStyleHook<PagerProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "pager",
});

/** A component which controls paging of data source data. */
export const Pager: FC<PagerProps> = (props) => {
  const { dataSource } = props;
  const { mode, styles, StyleModeBoundary } = useStyles(props);
  const { status, busy, page, pageSize, totalRows, maxRows, hasNextPage, first, previous, next, last } = dataSource;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      styles.Pager,
      "pager",
      {
        loading: busy,
        ready: status === "success",
      },
    ],
    testIdPrefix: "pager",
  });

  const minimumRow = page * pageSize + 1;
  let maximumRow = (page + 1) * pageSize;
  if (totalRows !== -1 && maximumRow > totalRows) maximumRow = totalRows;

  const pageRange = `${minimumRow} - ${maximumRow}`;

  return (
    <StyleModeBoundary mode={mode}>
      <div {...baseHtmlProps} aria-label="Pagination Navigation" className={styles.PagerContent}>
        <Shelf spacing="small">
          <Shelf spacing="small">
            {status !== "initializing" && (
              <div data-testid="pager-results" aria-live="polite" aria-atomic="true" role="status">
                <span className={styles["sr-only"]}>Page navigation results:</span>
                {totalRows > 0 && (
                  <>
                    Showing {pageRange} of {totalRows === -1 ? `${maxRows}+` : totalRows} result{totalRows !== 1 ? "s" : ""}
                  </>
                )}
              </div>
            )}
          </Shelf>
          <Shelf.Spacer />
          {pageSize < totalRows && (
            <div role="navigation" aria-label="Pagination Controls">
              <Shelf spacing="small" alignment="span">
                <Button appearance="secondary" tooltip="First page" onClick={first} icon="chevron-start" disabled={page === 0} testId="page-to-first" aria-label="Go to first page" />
                <Button appearance="secondary" tooltip="Previous page" onClick={previous} icon="chevron-left" disabled={page === 0} testId="page-to-previous" aria-label="Go to previous page" />
                <Button appearance="secondary" tooltip="Next page" onClick={next} icon="chevron-right" disabled={!hasNextPage} testId="page-to-next" aria-label="Go to next page" />
                <Button appearance="secondary" tooltip="Last page" onClick={last} icon="chevron-end" disabled={!hasNextPage} testId="page-to-last" aria-label="Go to last page" />
              </Shelf>
            </div>
          )
          }
        </Shelf>
      </div>
    </StyleModeBoundary>
  );
};
